<template>
  <submitted-data>
    <div class="flex flex-col space-y-4">
      <div class="flex space-x-4">
        <div class="h-4 w-4">
          <icon-base :icon="IconCompanySmallNoBg" />
        </div>
        <div class="row">
          <div
            v-for="item in companyRequestInfoData"
            :key="item.label"
            class="row-item"
          >
            <span class="text-label">{{ item.label }}</span>
            <span class="text-headline">{{ item.value }}</span>
          </div>
        </div>
      </div>
      <div class="flex space-x-4">
        <div class="h-4 w-4">
          <icon-base :icon="IconProfileSmall" />
        </div>
        <div class="row">
          <div
            v-for="item in personalRequestInfoData"
            :key="item.label"
            class="row-item"
          >
            <span class="text-label">{{ item.label }}</span>
            <span class="text-headline">{{ item.value }}</span>
          </div>
        </div>
      </div>
    </div>
  </submitted-data>
</template>
<script setup lang="ts">
import { type PropType, computed } from "vue";
import { useI18n } from "vue-i18n";

import { validateString } from "@/helpers/common";
import { formatAddress } from "@/helpers/formatting";

import IconCompanySmallNoBg from "@/components/icons/IconCompanySmallNoBg.vue";
import IconProfileSmall from "@/components/icons/IconProfileSmall.vue";
import IconBase from "@/components/ui/IconBase.vue";
import SubmittedData from "@/views/deals/components/shared/SubmittedData.vue";

import type { SearchBy } from "@/models/commercialData/lexisnexis/LexisNexisSmallBusinessBlendedCreditScoreRequestData";

const props = defineProps({
  requestData: {
    type: Object as PropType<SearchBy>,
    required: true,
  },
});

const personalRequestInfo = computed(
  () => props.requestData.AuthorizedRep1 || null
);
const companyRequestInfo = computed(() => props.requestData.Company || null);

const { t } = useI18n();

const companyRequestInfoData = computed(() => {
  if (!companyRequestInfo.value) {
    return [];
  }

  return [
    {
      label: t("COMMON.COMPANY_NAME"),
      value: validateString(companyRequestInfo.value.CompanyName),
    },
    {
      label: t("COMMON.ADDRESS"),
      value: formatAddress({
        address_line: companyRequestInfo.value.Address.StreetAddress1,
        address_line2: companyRequestInfo.value.Address.StreetAddress2,
        city: companyRequestInfo.value.Address.City,
        state: companyRequestInfo.value.Address.State,
        zip: companyRequestInfo.value.Address.Zip5,
      }),
    },
    {
      label: t("COMMON.PHONE"),
      value: validateString(companyRequestInfo.value.Phone),
    },
    {
      label: t("COMMON.FEIN"),
      value: validateString(companyRequestInfo.value.FEIN),
    },
  ];
});

const personalRequestInfoData = computed(() => {
  if (!personalRequestInfo.value) {
    return [];
  }

  return [
    {
      label: t("COMMON.FIRST_NAME"),
      value: validateString(personalRequestInfo.value.Name.First),
    },
    {
      label: t("COMMON.LAST_NAME"),
      value: validateString(personalRequestInfo.value.Name.Last),
    },
    {
      label: t("COMMON.ADDRESS"),
      value: formatAddress({
        address_line: personalRequestInfo.value.Address.StreetAddress1,
        address_line2: personalRequestInfo.value.Address.StreetAddress2,
        city: personalRequestInfo.value.Address.City,
        state: personalRequestInfo.value.Address.State,
        zip: personalRequestInfo.value.Address.Zip5,
      }),
    },
  ];
});
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.row {
  @apply grid grid-cols-4 gap-5;
}

.row-item {
  @apply flex flex-col gap-1;
}
</style>
